'use client';

import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { toast } from 'sonner';

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { usePrV2Summary } from '@/data/v2/pr/use-pr-v2-summary';
import { readHeadBranchName } from '@/lib/pr-v2/action-helpers';
import { copyToClipboard } from '@/utils/clipboard';

type PrV2PullRequestContextMenuProps = {
  owner: string;
  repo: string;
  number: string;
  children: ReactNode;
};

function readCopyValue(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

export function PrV2PullRequestContextMenu({
  owner,
  repo,
  number,
  children,
}: PrV2PullRequestContextMenuProps) {
  const summaryQuery = usePrV2Summary(owner, repo, number);
  const pullRequest = summaryQuery.data?.pullRequest;
  const githubUrl = readCopyValue(pullRequest?.url);
  const title = readCopyValue(pullRequest?.title);
  const branchName = readCopyValue(
    readHeadBranchName(pullRequest?.metadata, pullRequest?.metadataSummary?.headRef),
  );
  const checkoutCommand = `gh pr checkout ${number}`;

  const openOnGitHub = useCallback(() => {
    if (!githubUrl) {
      toast.error('GitHub URL not available');
      return;
    }

    window.open(githubUrl, '_blank', 'noopener,noreferrer');
  }, [githubUrl]);

  const copyValue = useCallback(async (value: string, successMessage: string) => {
    if (!value) {
      toast.error('Nothing to copy');
      return;
    }

    await copyToClipboard(value, {
      successMessage,
      errorMessage: 'Failed to copy',
    });
  }, []);

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="min-w-56">
        <ContextMenuItem onSelect={openOnGitHub} disabled={!githubUrl}>
          Open in GitHub
          <ContextMenuShortcut>⇧G</ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={() => void copyValue(githubUrl, 'GitHub URL copied')}>
          Copy link
          <ContextMenuShortcut>C L</ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => void copyValue(branchName, 'Branch name copied')}>
          Copy branch name
          <ContextMenuShortcut>C B</ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuItem
          onSelect={() => void copyValue(checkoutCommand, 'Checkout command copied')}
        >
          Copy checkout command
        </ContextMenuItem>
        <ContextMenuItem
          onSelect={() => {
            void copyValue(
              title && githubUrl ? `[${title}](${githubUrl})` : '',
              'Title copied as link',
            );
          }}
        >
          Copy title as link
          <ContextMenuShortcut>C T</ContextMenuShortcut>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
